// Story saas-PDF-C: multi-step modal de configuração do relatório analítico.
// Step 1: filtros (date range + score range se aplicável)
// Step 2: campos agregáveis (checkboxes default ON)
// Step 3: opções (appendix default OFF; anonimização default ON, disabled se appendix OFF)

import React, { useState, useMemo, useEffect } from 'react';
import { Loader2, ChevronLeft, ChevronRight, FileBarChart2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../Button';
import { Input } from '../ui/Input';
import { Checkbox } from '../ui/Checkbox';
import type { Form, FormField, FormResponse } from '../../types/forms';
import { isAggregatable } from '../../lib/pdf/reportAggregators';
import type { ReportConfig } from '../../lib/pdf/ReportPDFTemplate';

export interface ReportConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  form: Form;
  responses: FormResponse[];
  onExport: (config: ReportConfig) => void | Promise<void>;
  /** Quando true, o modal mostra progress indicator e bloqueia steps (durante generate). */
  isGenerating?: boolean;
  /** Progress opcional 0-100 durante generate (usado pelo caller). */
  progressLabel?: string | null;
}

const MAX_RESPONSES = 1000;

type StepIdx = 1 | 2 | 3;

export const ReportConfigModal: React.FC<ReportConfigModalProps> = ({
  isOpen,
  onClose,
  form,
  responses,
  onExport,
  isGenerating = false,
  progressLabel = null,
}) => {
  const aggFields = useMemo(() => (form.fields || []).filter(isAggregatable), [form.fields]);

  const hasScoreField = useMemo(
    () =>
      (form.fields || []).some(f =>
        ['rating', 'opinion_scale', 'nps', 'number'].includes(f.type)
      ),
    [form.fields]
  );

  const [step, setStep] = useState<StepIdx>(1);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [scoreMin, setScoreMin] = useState<string>('');
  const [scoreMax, setScoreMax] = useState<string>('');
  const [enabledFields, setEnabledFields] = useState<Set<string>>(
    () => new Set(aggFields.map(f => f.id))
  );
  const [includeAppendix, setIncludeAppendix] = useState(false);
  const [anonymize, setAnonymize] = useState(true);

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setDateFrom('');
      setDateTo('');
      setScoreMin('');
      setScoreMax('');
      setEnabledFields(new Set(aggFields.map(f => f.id)));
      setIncludeAppendix(false);
      setAnonymize(true);
    }
  }, [isOpen, aggFields]);

  // Filtered count preview
  const filteredCount = useMemo(() => {
    let list = responses;
    if (dateFrom) {
      const from = new Date(dateFrom).getTime();
      list = list.filter(r => new Date(r.created_at).getTime() >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo).getTime() + 24 * 60 * 60 * 1000 - 1;
      list = list.filter(r => new Date(r.created_at).getTime() <= to);
    }
    return list.length;
  }, [responses, dateFrom, dateTo]);

  const willSample = filteredCount > MAX_RESPONSES;

  const toggleField = (id: string) => {
    setEnabledFields(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const canGoNext: boolean = (() => {
    if (step === 1) return filteredCount > 0;
    if (step === 2) return enabledFields.size > 0;
    return true;
  })();

  const handleNext = () => {
    if (step < 3) setStep((step + 1) as StepIdx);
  };

  const handleBack = () => {
    if (step > 1) setStep((step - 1) as StepIdx);
  };

  const handleGenerate = async () => {
    const config: ReportConfig = {
      dateRange:
        dateFrom || dateTo
          ? {
              from: dateFrom || undefined,
              to: dateTo || undefined,
            }
          : undefined,
      scoreRange:
        scoreMin || scoreMax
          ? {
              min: scoreMin ? Number(scoreMin) : undefined,
              max: scoreMax ? Number(scoreMax) : undefined,
            }
          : undefined,
      enabledFields: Array.from(enabledFields),
      includeAppendix,
      anonymize,
    };
    await onExport(config);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isGenerating ? () => {} : onClose}
      title="Gerar Relatório Analítico"
      size="lg"
      showCloseButton={!isGenerating}
      closeOnEscape={!isGenerating}
      closeOnOverlayClick={!isGenerating}
    >
      <div className="space-y-5">
        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between text-xs text-neutral-400 mb-2">
            <span>Passo {step} de 3</span>
            <span>
              {step === 1 && 'Filtros'}
              {step === 2 && 'Campos Agregáveis'}
              {step === 3 && 'Opções'}
            </span>
          </div>
          <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>

        {/* Step 1 — Filtros */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-neutral-300 mb-1">
                Ajuste o período ou intervalo de pontuação. Por padrão, inclui todas as
                respostas selecionadas.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                type="date"
                label="De"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                disabled={isGenerating}
              />
              <Input
                type="date"
                label="Até"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                disabled={isGenerating}
              />
            </div>
            {hasScoreField && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  type="number"
                  label="Score mín."
                  value={scoreMin}
                  onChange={e => setScoreMin(e.target.value)}
                  placeholder="0"
                  disabled={isGenerating}
                />
                <Input
                  type="number"
                  label="Score máx."
                  value={scoreMax}
                  onChange={e => setScoreMax(e.target.value)}
                  placeholder="100"
                  disabled={isGenerating}
                />
              </div>
            )}

            <div className="p-3 rounded-lg bg-neutral-900/50 border border-neutral-700/50 text-sm">
              <span className="text-neutral-400">Respostas após filtros: </span>
              <span className="text-white font-semibold">{filteredCount}</span>
              {filteredCount === 0 && (
                <p className="mt-1 text-red-400 text-xs">
                  Nenhuma resposta no período selecionado. Ajuste os filtros.
                </p>
              )}
            </div>

            {willSample && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300 leading-relaxed">
                <strong>Amostragem ativa.</strong> Relatório incluirá as 1.000 respostas mais
                recentes dentro do período selecionado. Para relatórios maiores, exporte via CSV.
              </div>
            )}
          </div>
        )}

        {/* Step 2 — Fields Agregáveis */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-neutral-300">
                Selecione os campos a incluir no relatório. Todos pré-marcados por padrão.
              </p>
            </div>
            <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
              {aggFields.length === 0 ? (
                <p className="text-sm text-neutral-500 italic py-4 text-center">
                  Este formulário não possui campos agregáveis (choices, scales, ratings,
                  numbers, datas).
                </p>
              ) : (
                aggFields.map(f => (
                  <FieldCheckbox
                    key={f.id}
                    field={f}
                    checked={enabledFields.has(f.id)}
                    onToggle={() => toggleField(f.id)}
                    disabled={isGenerating}
                  />
                ))
              )}
            </div>
            {enabledFields.size === 0 && aggFields.length > 0 && (
              <p className="text-xs text-red-400">Selecione ao menos um campo.</p>
            )}
          </div>
        )}

        {/* Step 3 — Opções */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-neutral-300">
                Opções finais antes de gerar o relatório.
              </p>
            </div>

            <div className="p-3 rounded-lg border border-neutral-700/50 bg-neutral-900/50 space-y-3">
              <Checkbox
                label="Incluir apêndice com 20 respostas de texto mais recentes"
                helpText="Adiciona uma seção ao final do PDF com respostas raw dos campos de texto (text/textarea)."
                checked={includeAppendix}
                onChange={e => setIncludeAppendix(e.target.checked)}
                disabled={isGenerating}
              />
              <div className={includeAppendix ? '' : 'opacity-50 pointer-events-none'}>
                <Checkbox
                  label="Anonimizar email e nome no apêndice (LGPD)"
                  helpText={
                    includeAppendix
                      ? 'Substitui emails por "***@***" e nomes por "Respondente #N".'
                      : 'Desativado — ative o apêndice para usar.'
                  }
                  checked={anonymize}
                  onChange={e => setAnonymize(e.target.checked)}
                  disabled={!includeAppendix || isGenerating}
                />
              </div>
            </div>

            <div className="p-3 rounded-lg bg-primary-500/5 border border-primary-500/20 text-xs text-neutral-300 leading-relaxed">
              <div className="flex items-start gap-2">
                <FileBarChart2 className="w-4 h-4 text-primary-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium mb-1">Pronto pra gerar</p>
                  <p>
                    {filteredCount > MAX_RESPONSES ? MAX_RESPONSES : filteredCount} resposta
                    {(filteredCount > MAX_RESPONSES ? MAX_RESPONSES : filteredCount) === 1 ? '' : 's'} ·{' '}
                    {enabledFields.size} campo{enabledFields.size === 1 ? '' : 's'} agregado
                    {enabledFields.size === 1 ? '' : 's'}
                    {includeAppendix ? ' · apêndice ativo' : ''}
                  </p>
                </div>
              </div>
            </div>

            {isGenerating && (
              <div className="p-3 rounded-lg bg-neutral-900/70 border border-neutral-700 text-sm flex items-center gap-3">
                <Loader2 className="w-4 h-4 text-primary-400 animate-spin" />
                <span className="text-neutral-300">
                  {progressLabel ?? 'Gerando relatório... isso pode levar alguns segundos.'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Footer nav */}
        <div className="flex items-center justify-between pt-2 border-t border-neutral-700/50">
          <Button variant="outline" onClick={onClose} disabled={isGenerating}>
            Cancelar
          </Button>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button variant="outline" onClick={handleBack} disabled={isGenerating}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Voltar
              </Button>
            )}
            {step < 3 ? (
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={!canGoNext || isGenerating}
              >
                Próximo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating || enabledFields.size === 0 || filteredCount === 0}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 min-h-[40px]"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <FileBarChart2 className="w-4 h-4" />
                    Gerar Relatório
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

const FieldCheckbox: React.FC<{
  field: FormField;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
}> = ({ field, checked, onToggle, disabled }) => (
  <label
    className={`flex items-start gap-3 p-2.5 rounded-lg border border-neutral-800 hover:border-neutral-700 bg-neutral-900/30 cursor-pointer transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed' : ''
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={onToggle}
      disabled={disabled}
      className="mt-0.5 w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-primary-500 focus:ring-primary-500/50"
    />
    <div className="flex-1 min-w-0">
      <div className="text-sm text-white truncate">{field.label}</div>
      <div className="text-xs text-neutral-500">{field.type}</div>
    </div>
  </label>
);
